/**
 * Links an eligibility program card to the CoSell pipeline, pre-filtered to
 * that program.
 *
 * The pipeline reads its filters straight off the URL, so a link is all the
 * hand-off needs — no shared state between the two pages. This is the one spot
 * in the package that knows a host route; keep it here so a route change is a
 * single edit.
 */

const CO_SELL_REFERRALS_PATH = (accountId: string) =>
  `/oppsync/aws/${accountId}/cosell/outgoing-referrals`

const FUNDING_PROGRAM_PARAM = 'funding_program'

/**
 * Eligibility programs are reported under their canonical names, while the
 * pipeline filter lists the shorter labels the referrals API documents. A
 * canonical name can fan out to several filter labels: the POC card stands for
 * the whole Proof-of-Concept family, so it selects every POC option.
 */
const PROGRAM_FILTER_LABELS: Record<string, string[]> = {
  'MPOPP Activate': ['MPOPP Activate'],
  'MPOPP Grow': ['MPOPP Grow'],
  'Proof Of Concept Funding program': [
    'POC Standard',
    'POC Non-standard',
    'POC Template',
    'POC Gen AI',
  ],
  'Proof Of Concept Funding Template': ['POC Template'],
  'SCA Non-Standard Proof of Concept': ['POC Non-standard'],
  'SCA GenAI Proof Of Concept Funding': ['POC Gen AI'],
}

export const fundingProgramFilterLabels = (programName: string): string[] =>
  PROGRAM_FILTER_LABELS[programName.trim()] ?? []

/**
 * Build the pre-filtered pipeline link, or null when the program has no
 * matching filter option (an unmapped program would otherwise land on an
 * unfiltered list, which reads as a broken link).
 */
export const coSellReferralsLink = (
  accountId: string,
  programName: string
): string | null => {
  const labels = fundingProgramFilterLabels(programName)
  if (!accountId || labels.length === 0) return null
  const params = new URLSearchParams({
    [FUNDING_PROGRAM_PARAM]: labels.join(','),
  })
  return `${CO_SELL_REFERRALS_PATH(accountId)}?${params.toString()}`
}
